import { useState } from 'react';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Divider from '@mui/material/Divider';
import Paper from '@mui/material/Paper';
import { alpha } from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import FingerprintIcon from '@mui/icons-material/Fingerprint';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import GroupsIcon from '@mui/icons-material/Groups';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { LOGIN_LINK_TTL_MS, isValidEmail } from '../domain';
import { isPasskeyCancelled, passkeysSupported } from '../data/passkeyClient';
import { useAuth } from '../state/AuthContext';
import { useTranslation } from '../state/I18nContext';
import { BrandLogo } from './BrandLogo';
import { signupPath } from './public/signupPath';

// The email path walks forward through these; the passkey path skips them all.
type Step = 'email' | 'method' | 'password';

// Sign-in screen. Shown whenever there is no valid session (see `App.tsx`).
//
// Two different people land here and only one of them has an account, so the
// first step separates the two intents before either can be mistaken for the
// other: a club that is not on the platform yet is sent to the public sign-up
// wizard on `/signup` (`features/company-registration.md`), named in its own
// panel above the sign-in form; athletes and coaches are told they arrive by
// club invitation (`features/athlete-access.md`) rather than by signing a club
// up. Everything below the divider is the sign-in flow proper.
//
// The email path is a two-step flow: enter the email first, then choose how to
// prove it — a magic link (see `features/passwordless-login.md`) or the
// password. Alongside step one sits the single-tap passkey sign-in (see
// `features/passkey-login.md`). A fourth shape appears when a magic-link token
// is present in the URL: a "complete sign-in" button that redeems it on an
// explicit click (so an email link-scanner cannot burn the single-use token).
// Keeps no token logic of its own — it delegates to `useAuth`.
export function LoginScreen() {
  const {
    login,
    loginWithPasskey,
    requestLoginLink,
    hasPendingLink,
    redeemPendingLink,
    linkError,
  } = useAuth();
  const { t } = useTranslation();

  const [step, setStep] = useState<Step>('email');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [linkSent, setLinkSent] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const expiryMinutes = Math.round(LOGIN_LINK_TTL_MS / 60000);
  const emailValid = isValidEmail(email.trim());
  const passwordValid = password.length > 0;

  const continueWithEmail = () => {
    if (!emailValid || submitting) return;
    setError(null);
    setStep('method');
  };

  // Back to step one, keeping the typed email so a typo costs one edit.
  const changeEmail = () => {
    setStep('email');
    setPassword('');
    setError(null);
    setLinkSent(false);
  };

  const submitLink = async () => {
    if (submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      await requestLoginLink(email.trim());
      setLinkSent(true);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : t('admin.login.link.failed'));
    } finally {
      setSubmitting(false);
    }
  };

  const submitPassword = async () => {
    if (!passwordValid || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      await login(email.trim(), password);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : t('admin.login.failed'));
      setSubmitting(false);
    }
  };

  const submitPasskey = async () => {
    if (submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      await loginWithPasskey();
    } catch (e: unknown) {
      // Backing out of the browser's passkey prompt is a non-event.
      if (!isPasskeyCancelled(e)) {
        setError(e instanceof Error ? e.message : t('admin.login.passkey.failed'));
      }
      setSubmitting(false);
    }
  };

  // "Signing in as <email> (change)" — the header of every step past the first.
  const emailHeader = (
    <Stack
      direction="row"
      spacing={1}
      alignItems="baseline"
      flexWrap="wrap"
      sx={{ mb: 2 }}
    >
      <Typography variant="body2" color="text.secondary">
        {t('admin.login.signingAs')}
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 600, minWidth: 0, overflowWrap: 'anywhere' }}>
        {email.trim()}
      </Typography>
      <Button size="small" variant="text" onClick={changeEmail} disabled={submitting}>
        {t('admin.login.change')}
      </Button>
    </Stack>
  );

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        p: 2,
      }}
    >
      <Card sx={{ width: '100%', maxWidth: 420, border: 1, borderColor: 'divider' }}>
        <CardContent>
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1 }}>
            <BrandLogo height={30} />
            <Typography variant="h5">{t('admin.login.title')}</Typography>
          </Stack>

          {hasPendingLink ? (
            // A magic link was opened: finish it on an explicit click.
            <Stack spacing={2} sx={{ mt: 1 }}>
              <Typography variant="body2" color="text.secondary">
                {t('admin.login.link.completeBody')}
              </Typography>
              <Button
                size="large"
                variant="contained"
                startIcon={<LockOutlinedIcon />}
                onClick={() => void redeemPendingLink()}
              >
                {t('admin.login.link.complete')}
              </Button>
            </Stack>
          ) : linkSent ? (
            // The magic link is on its way; nothing left to do on this device.
            <Stack spacing={2} sx={{ mt: 1 }}>
              <Alert severity="success">
                {t('admin.login.link.sent', { minutes: expiryMinutes })}
              </Alert>
              <Button variant="text" onClick={changeEmail}>
                {t('admin.login.link.resend')}
              </Button>
            </Stack>
          ) : step === 'email' ? (
            <>
              {/* Club sign-up (see `features/company-registration.md`) is a
                  separate, unauthenticated wizard on its own `/signup` route —
                  a shareable URL, so this panel only points at it. It leads the
                  card because a club owner reaching for the email field below
                  has no account to sign in with, which is the confusion this
                  layout exists to remove. Secondary colour keeps it distinct
                  from the sign-in CTA rather than competing with it. */}
              <Paper
                variant="outlined"
                sx={(muiTheme) => ({
                  p: 2,
                  mt: 1,
                  mb: 2,
                  borderColor: alpha(muiTheme.palette.secondary.main, 0.45),
                  backgroundColor: alpha(muiTheme.palette.secondary.main, 0.1),
                })}
              >
                <Stack spacing={1.25}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <GroupsIcon fontSize="small" color="secondary" />
                    <Typography variant="subtitle1">
                      {t('admin.login.signup.title')}
                    </Typography>
                  </Stack>
                  <Typography variant="body2" color="text.secondary">
                    {t('admin.login.signup.body')}
                  </Typography>
                  <Button
                    variant="contained"
                    color="secondary"
                    endIcon={<ArrowForwardIcon />}
                    href={signupPath()}
                  >
                    {t('admin.login.signup.action')}
                  </Button>
                  {/* Athletes and coaches never sign a club up — they are
                      invited by theirs (see `features/athlete-access.md`) — so
                      say it here, where they would otherwise click above. */}
                  <Typography variant="caption" color="text.secondary">
                    {t('admin.login.signup.athletes')}
                  </Typography>
                </Stack>
              </Paper>

              <Divider sx={{ mb: 2 }}>{t('admin.login.signInDivider')}</Divider>

              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {t('admin.login.tagline')}
              </Typography>

              {/* A failed magic-link redemption (invalid / expired) lands here. */}
              {linkError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {linkError}
                </Alert>
              )}

              <Box
                component="form"
                onSubmit={(e) => {
                  e.preventDefault();
                  continueWithEmail();
                }}
              >
                <Stack spacing={2}>
                  {error && <Alert severity="error">{error}</Alert>}
                  <TextField
                    label={t('admin.login.email')}
                    type="email"
                    autoComplete="username"
                    fullWidth
                    autoFocus
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                  <Button
                    type="submit"
                    size="large"
                    variant="contained"
                    disabled={!emailValid || submitting}
                  >
                    {t('admin.login.continue')}
                  </Button>
                </Stack>
              </Box>

              {/* Passkey sign-in: one tap with a credential stored on this
                  device (see `features/passkey-login.md`) — no email needed.
                  Hidden when the browser cannot do WebAuthn at all. */}
              {passkeysSupported() && (
                <>
                  <Divider sx={{ my: 2 }}>{t('admin.login.passkey.divider')}</Divider>
                  <Button
                    size="large"
                    variant="outlined"
                    fullWidth
                    startIcon={<FingerprintIcon />}
                    disabled={submitting}
                    onClick={() => void submitPasskey()}
                  >
                    {t('admin.login.passkey.button')}
                  </Button>
                </>
              )}
            </>
          ) : step === 'method' ? (
            <>
              {emailHeader}
              <Stack spacing={2}>
                {error && <Alert severity="error">{error}</Alert>}
                <Typography variant="body2" color="text.secondary">
                  {t('admin.login.method.title')}
                </Typography>
                <Button
                  size="large"
                  variant="contained"
                  startIcon={<EmailOutlinedIcon />}
                  disabled={submitting}
                  onClick={() => void submitLink()}
                >
                  {submitting ? t('admin.login.link.sending') : t('admin.login.link.send')}
                </Button>
                <Button
                  size="large"
                  variant="outlined"
                  startIcon={<LockOutlinedIcon />}
                  disabled={submitting}
                  onClick={() => {
                    setError(null);
                    setStep('password');
                  }}
                >
                  {t('admin.login.method.password')}
                </Button>
              </Stack>
            </>
          ) : (
            <>
              {emailHeader}
              <Box
                component="form"
                onSubmit={(e) => {
                  e.preventDefault();
                  void submitPassword();
                }}
              >
                <Stack spacing={2}>
                  {error && <Alert severity="error">{error}</Alert>}
                  {/* Hidden username field so password managers pair the
                      password with the account chosen in step one. */}
                  <input
                    type="email"
                    autoComplete="username"
                    value={email.trim()}
                    readOnly
                    hidden
                  />
                  <TextField
                    label={t('admin.login.password')}
                    type="password"
                    autoComplete="current-password"
                    fullWidth
                    autoFocus
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                  <Button
                    type="submit"
                    size="large"
                    variant="contained"
                    startIcon={<LockOutlinedIcon />}
                    disabled={!passwordValid || submitting}
                  >
                    {submitting ? t('admin.login.signingIn') : t('admin.login.signIn')}
                  </Button>
                  <Button variant="text" disabled={submitting} onClick={() => void submitLink()}>
                    {t('admin.login.linkInstead')}
                  </Button>
                </Stack>
              </Box>
            </>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}
